import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, GitBranch, ExternalLink, AlertCircle, Shield, Clock, Globe2 } from 'lucide-react';
import { Spinner } from '../design-system';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { useScanProgress } from '../contexts/ScanProgressContext';
import { apiEndpoints, ApiError } from '../utils/api';
import { ApplicationResponse } from '../types/application';
import { getApplicationDisplayTitle } from '../utils/repository';
import { Finding, FindingListResponse } from '../types/finding';
import { ScanBatch, ScanListResponse } from '../types/scan';
import ApplicationProfileEditor from '../components/applications/ApplicationProfileEditor';
import ApplicationScanScheduleSection from '../components/applications/ApplicationScanScheduleSection';
import BranchSelector from '../components/applications/BranchSelector';
import RepositorySandboxConfigSection from '../components/applications/RepositorySandboxConfigSection';
import PublicScanPageSettings from '../components/applications/PublicScanPageSettings';
import CreateScanModal from '../components/scans/CreateScanModal';
import { compareBySeverity } from '../components/findings/severityDisplay';
import RepositoryOverview from './repository-overview/RepositoryOverview';
import { OverviewStats, initialStats } from './repository-overview/types';
import { getFirstName } from './repository-overview/greeting';
import { ProviderIcon, normalizeProvider, providerLabel } from '../config/providers';

type TabId = 'overview' | 'repositories' | 'security-context' | 'schedules' | 'public-status';

const ApplicationDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId } = useParams<{ applicationId: string }>();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();
  const { displayName } = useAuth();
  const api = useAuthenticatedApi();

  const firstName = getFirstName(displayName);

  const isReadOnly =
    !!currentOrganization?.is_demo ||
    currentOrganization?.current_user_role === 'viewer';

  // Determine active tab from route path
  const activeTab: TabId = useMemo(() => {
    if (location.pathname.endsWith('/settings/security-context')) return 'security-context';
    if (location.pathname.endsWith('/settings/schedules')) return 'schedules';
    if (location.pathname.endsWith('/settings/public-status')) return 'public-status';
    if (location.pathname.endsWith('/settings/source')) return 'repositories';
    return 'overview';
  }, [location.pathname]);

  const [application, setApplication] = useState<ApplicationResponse | null>(null);
  const [stats, setStats] = useState<OverviewStats>(initialStats);
  const [recentScans, setRecentScans] = useState<ScanBatch[]>([]);
  const [recentFindings, setRecentFindings] = useState<Finding[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Tracks the first findings/scan stats load so the overview shows a loading state
  // instead of flashing the "run your first scan" card before stats arrive.
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanModalOpen, setScanModalOpen] = useState(false);

  const loadApplication = useCallback(async () => {
    if (!currentOrganization?.id || !applicationId) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<ApplicationResponse>(
        apiEndpoints.applications.get(currentOrganization.id, applicationId)
      );
      setApplication(response);
      setIsLoading(false);
    } catch (err: any) {
      // The application id in the URL doesn't belong to the active organization
      // — a stale/cross-org id left behind by a workspace switch or the browser
      // back button. Send the user to the /repositories resolver, which lands
      // them on a valid repository for the active organization instead of a
      // dead-end "Application not found" page. Keep the spinner up while we
      // navigate away so the error card never flashes.
      if (err instanceof ApiError && err.status === 404) {
        navigate('/repositories', { replace: true });
        return;
      }
      console.error('Failed to load application:', err);
      setError(err.message || 'Failed to load repository');
      setIsLoading(false);
    }
  }, [api, currentOrganization?.id, applicationId, navigate]);

  const loadFindingStats = useCallback(async () => {
    if (!applicationId || !currentOrganization?.id) return;

    try {
      // Load the data the overview needs in parallel
      const [scansResponse, findingsResponse] = await Promise.all([
        api.get<ScanListResponse>(
          `${apiEndpoints.scans.list()}?application_id=${applicationId}&limit=100`
        ).catch(() => null),
        api.get<FindingListResponse>(
          `${apiEndpoints.findings.byApplication(applicationId)}?limit=100`
        ).catch(() => null),
      ]);

      // Process scans
      if (scansResponse) {
        setRecentScans(scansResponse.batches.slice(0, 5));
        setStats((prev) => ({
          ...prev,
          scans: {
            total: scansResponse.stats.total,
            pending: scansResponse.stats.pending,
            running: scansResponse.stats.running,
            succeeded: scansResponse.stats.succeeded,
            failed: scansResponse.stats.failed,
          },
        }));
      }

      // Process findings
      if (findingsResponse) {
        setRecentFindings(
          findingsResponse.findings
            .filter((f) => f.status !== 'ignored' && f.status !== 'false_positive')
            .sort(compareBySeverity) // surface the most critical findings first
            .slice(0, 5)
        );

        const findingsBySeverity = findingsResponse.findings.reduce(
          (acc, finding) => {
            acc[finding.severity] = (acc[finding.severity] || 0) + 1;
            return acc;
          },
          { critical: 0, high: 0, medium: 0, low: 0 } as Record<string, number>
        );

        const findingsByStatus = findingsResponse.findings.reduce(
          (acc, finding) => {
            acc[finding.status] = (acc[finding.status] || 0) + 1;
            return acc;
          },
          { open: 0, needs_manual_review: 0, resolved: 0, ignored: 0, false_positive: 0 } as Record<string, number>
        );

        setStats((prev) => ({
          ...prev,
          findings: {
            total: findingsResponse.findings.length,
            bySeverity: {
              critical: findingsBySeverity.critical || 0,
              high: findingsBySeverity.high || 0,
              medium: findingsBySeverity.medium || 0,
              low: findingsBySeverity.low || 0,
            },
            byStatus: {
              open: findingsByStatus.open || 0,
              needsReview: findingsByStatus.needs_manual_review || 0,
              resolved: findingsByStatus.resolved || 0,
              ignored: findingsByStatus.ignored || 0,
              falsePositive: findingsByStatus.false_positive || 0,
            },
          },
        }));
      }

    } catch (err: any) {
      console.error('Failed to load finding stats:', err);
      // Silently fail - stats are optional
    } finally {
      setStatsLoaded(true);
    }
  }, [api, applicationId, currentOrganization?.id]);

  // Load application data only when organization or applicationId changes
  // Using a separate effect with stable dependencies to prevent re-fetches on tab switches
  useEffect(() => {
    if (!orgLoading && currentOrganization?.id && applicationId) {
      setStatsLoaded(false);
      loadApplication();
      loadFindingStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgLoading, currentOrganization?.id, applicationId]);

  // Live progress for the repository's active scan (drives the overview "scanning" view).
  // Shares the single app-shell poller via context rather than starting its own.
  const repoScanProgress = useScanProgress();
  const scanProgress = {
    // Guard against another repository's progress bleeding in while context catches up.
    active: repoScanProgress.state === 'running' && repoScanProgress.applicationId === applicationId,
    percent: repoScanProgress.percent,
    findingsCount: repoScanProgress.findingsCount,
    currentOperation: repoScanProgress.currentOperation,
  };

  // Refresh findings/scan stats once when a running scan finishes so the overview
  // moves on from the "scanning" view and surfaces findings automatically.
  const prevScanStateRef = useRef(repoScanProgress.state);
  useEffect(() => {
    if (prevScanStateRef.current === 'running' && repoScanProgress.state !== 'running') {
      loadFindingStats();
    }
    prevScanStateRef.current = repoScanProgress.state;
  }, [repoScanProgress.state, loadFindingStats]);

  // Optimistically show the running view right after onboarding hands off here, before
  // the first poll detects the queued batch. Captured once on mount; cleared once real
  // progress arrives and after a short fallback (so it can't stick if the scan failed to start).
  const cameFromOnboardingRef = useRef(!!(location.state as { firstScan?: boolean } | null)?.firstScan);
  const [expectFirstScan, setExpectFirstScan] = useState(cameFromOnboardingRef.current);
  useEffect(() => {
    if (!cameFromOnboardingRef.current) return;
    // Clear the flag from history so a refresh/back doesn't re-trigger the optimistic view.
    navigate(location.pathname, { replace: true, state: null });
    const timeout = window.setTimeout(() => setExpectFirstScan(false), 12000);
    return () => window.clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (scanProgress.active) setExpectFirstScan(false);
  }, [scanProgress.active]);

  if (orgLoading || isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size={32} />
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <button
          onClick={() => navigate('/repositories')}
          className="flex items-center gap-2 text-sm text-ui-text-tertiary hover:text-ui-text-primary dark:text-ui-text-tertiary dark:hover:text-ui-text-primary mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Repositories
        </button>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-ui-text-secondary mb-4">
            {error || 'Repository not found'}
          </p>
          <button
            onClick={loadApplication}
            className="px-4 py-2 text-sm font-medium text-brand-primary hover:text-brand-primaryDark dark:text-brand-primaryDarkMode"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const displayTitle = getApplicationDisplayTitle(application);

  const renderOverviewTab = () => (
    <RepositoryOverview
      application={application}
      stats={stats}
      recentScans={recentScans}
      recentFindings={recentFindings}
      isLoading={isLoading || !statsLoaded}
      firstName={firstName}
      onRunScan={() => setScanModalOpen(true)}
      onViewAllFindings={() => navigate(`/repositories/${applicationId}/findings`)}
      onOpenFinding={(findingId) => navigate(`/repositories/${applicationId}/findings/${findingId}`)}
      scanProgress={scanProgress}
      expectFirstScan={expectFirstScan}
    />
  );

  const renderRepositoriesTab = () => (
    <>
      {application.repositories.length === 0 ? (
        <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-8 text-center">
          <GitBranch className="w-10 h-10 text-ui-text-tertiary mx-auto mb-3" />
          <p className="text-ui-text-secondary">
            No repository configured yet.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {application.repositories.map((repo) => (
            <div
              key={repo.full_name}
              className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <ProviderIcon provider={repo.provider} className="w-4 h-4 text-ui-text-tertiary shrink-0" />
                    <span
                      className="font-medium text-ui-text-primary truncate min-w-0"
                      title={repo.full_name}
                    >
                      {repo.full_name}
                    </span>
                  </div>
                  {currentOrganization && (
                    <BranchSelector
                      organizationId={currentOrganization.id}
                      applicationId={applicationId!}
                      repoFullName={repo.full_name}
                      provider={normalizeProvider(repo.provider)}
                      currentBranch={repo.default_branch}
                      onBranchUpdated={(updated) => setApplication(updated)}
                      gitlabInstanceUrl={repo.provider === 'gitlab' && repo.html_url ? new URL(repo.html_url).origin : undefined}
                      readOnly={isReadOnly}
                    />
                  )}
                </div>
                {repo.html_url && (
                  <a
                    href={repo.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-brand-primary hover:text-brand-primaryDark dark:text-brand-primaryDarkMode transition-colors flex-shrink-0"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span className="sm:hidden">View</span>
                    <span className="hidden sm:inline">View on {providerLabel(repo.provider)}</span>
                  </a>
                )}
              </div>

              {/* Sandbox Configuration Section */}
              {currentOrganization && (
                <RepositorySandboxConfigSection
                  organizationId={currentOrganization.id}
                  applicationId={applicationId!}
                  repositoryFullName={repo.full_name}
                  repositoryProvider={normalizeProvider(repo.provider)}
                  repository={repo}
                  applicationRepositories={application.repositories}
                  onApplicationUpdated={(updatedApp) => setApplication(updatedApp)}
                  readOnly={isReadOnly}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );

  const renderSecurityContextTab = () => (
    <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-6">
      <div className="mb-4">
        {application.profile && (
          <span className="text-xs text-ui-text-tertiary">
            {application.profile.is_user_modified ? 'User-Modified' : 'AI-Derived'}
          </span>
        )}
      </div>
      <ApplicationProfileEditor
        organizationId={currentOrganization!.id}
        applicationId={applicationId!}
        profile={application.profile}
        onUpdate={(updatedApp) => setApplication(updatedApp)}
        readOnly={isReadOnly}
      />
    </div>
  );

  const renderSchedulesTab = () => (
    <>
      {currentOrganization && (
        <ApplicationScanScheduleSection
          organizationId={currentOrganization.id}
          applicationId={applicationId!}
        />
      )}
    </>
  );

  const renderPublicStatusTab = () => (
    <>
      {currentOrganization && (
        <PublicScanPageSettings
          organizationId={currentOrganization.id}
          applicationId={applicationId!}
          canManage={
            !isReadOnly &&
            (currentOrganization.current_user_role === 'owner' || currentOrganization.current_user_role === 'admin')
          }
        />
      )}
    </>
  );

  // Page title based on active tab
  const pageTitle = activeTab === 'repositories' ? 'Repositories' 
    : activeTab === 'security-context' ? 'Security Context'
    : activeTab === 'schedules' ? 'Schedules'
    : activeTab === 'public-status' ? 'Public Status'
    : displayTitle;

  const pageIcon = activeTab === 'repositories' ? GitBranch
    : activeTab === 'security-context' ? Shield
    : activeTab === 'schedules' ? Clock
    : activeTab === 'public-status' ? Globe2
    : GitBranch;

  const PageIcon = pageIcon;

  // The overview is a full-bleed design-system page with its own greeting header.
  if (activeTab === 'overview') {
    return (
      <>
        {renderOverviewTab()}
        <CreateScanModal
          isOpen={scanModalOpen}
          onClose={() => setScanModalOpen(false)}
          preselectedApplicationId={applicationId}
          onSuccess={() => {
            setScanModalOpen(false);
            loadFindingStats();
          }}
        />
      </>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-ui-background-tertiary rounded-xl flex items-center justify-center">
            <PageIcon className="w-6 h-6 text-ui-text-secondary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ui-text-primary">
              {pageTitle}
            </h1>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div>
        {activeTab === 'repositories' && renderRepositoriesTab()}
        {activeTab === 'security-context' && renderSecurityContextTab()}
        {activeTab === 'schedules' && renderSchedulesTab()}
        {activeTab === 'public-status' && renderPublicStatusTab()}
      </div>
    </div>
  );
};

export default ApplicationDetailPage;
